/**
 * components/assistant/AssistantChat.js — ХАБЭА ТУСЛАХ (дэлгэцийн доорх чат).
 *
 * Хэрэглэгч (2026-11-04): «туслах AI хэсгийг дэлгэцийн доор бусад чат шиг
 * харуул, /me дотор хэрэггүй». Layout-д НЭГ удаа суусан тул бүх хуудсанд.
 *
 *   • Нээхэд «Өнөөдрийн ажил»-ыг (/xab/assistant/today) чатын мессеж болгоно.
 *   • Асуулт бичвэл AI хариулна (/xab/assistant/chat): журам, заавраас
 *     хайсан заалт + тухайн хүний өнөөдрийн ажлыг ЭХ СУРВАЛЖ болгоно, тэнд
 *     байхгүйг зохиохгүй. AI унтраалттай бол олдсон заалтыг л харуулна.
 *   • «Уншиж өгөх» (TTS) нь ХАССАН (2026-11-04, хэрэглэгчийн шийдвэр).
 *   • Тоо нь өгөгдлийн сангаас; хугацаа хэтэрсэн ажил байвал товчин дээр
 *     улаан тэмдэг гарна.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Input, Spin, Tooltip } from "antd";
import {
  AlertOutlined,
  BellOutlined,
  BookOutlined,
  CalendarOutlined,
  CloseOutlined,
  EditOutlined,
  SendOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import HseAiIcon from "src/components/assistant/HseAiIcon";
import { assistantApi } from "src/api/assistantApi";
import { xabAskApi } from "src/api/xabApi";

// Анхаарал татах градиент — ногоон → цэнхэр (хэрэглэгч, 2026-11-04: «өнгийг
// өөр гоё болго анхаарал татах», дараа нь «ногоон цэнхэр өнгө оруул»).
// Цагаан бичвэртэй харьцуулахад тодрол хангалттай гүн өнгүүд.
const BRAND = "#0E7490";
const GRAD = "linear-gradient(135deg, #059669 0%, #0891B2 50%, #2563EB 100%)";
const PULSE_CSS =
  "@keyframes hseAsstPulse{0%{box-shadow:0 0 0 0 rgba(8,145,178,.55)}" +
  "70%{box-shadow:0 0 0 16px rgba(8,145,178,0)}100%{box-shadow:0 0 0 0 rgba(8,145,178,0)}}" +
  "@keyframes hseAsstIn{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}";
const ICON = {
  tasks: <UnorderedListOutlined />,
  to_assign: <AlertOutlined />,
  plan: <CalendarOutlined />,
  formal_sign: <EditOutlined />,
  morning: <TeamOutlined />,
  knowledge: <BookOutlined />,
  notify: <BellOutlined />,
};
const LEVEL = {
  danger: { fg: "#B91C1C", bg: "#FEF2F2", bd: "#FECACA" },
  warn: { fg: "#B45309", bg: "#FFFBEB", bd: "#FDE68A" },
  info: { fg: "#1F4E79", bg: "#EFF6FF", bd: "#BFDBFE" },
  ok: { fg: "#15803D", bg: "#F0FDF4", bd: "#BBF7D0" },
};
const CHIPS = [
  { key: "today", label: "Өнөөдрийн ажил" },
  { key: "hazard", label: "Аюул мэдээлэх" },
];
const TODAY_RE = /(өнөөдөр|ажил|даалгавар|юу хийх|мэдэгдэл)/i;
// «Зөрчлөө яаж арилгах вэ», «даалгавраа хэрхэн хийх» — ЗӨВХӨН асуусан үед
// даалгаврын жагсаалт + «Алхмаар харуулах» гарна (хэрэглэгч, 2026-11-05:
// «энэ мэдээллийг асуусан үед л хариул»). TODAY_RE-ээс ӨМНӨ шалгана.
const TASK_RE =
  /((зөрчил|зөрчл|даалгав|даалгавар)\S*\s+.*(арилга|засах|засна|хийх|гүйцэтгэ|биелүүл|биелэлт|алхам))|((арилга|засах|биелэлт)\S*\s+.*(зөрчил|зөрчл|даалгав))/i;

/** Өөр газраас (жш: /me-ийн танилцуулга) чатыг нээх — нэг л товч бий */
const OPEN_EVENT = "hse-assistant-open";
export const openAssistant = () => {
  try {
    window.dispatchEvent(new Event(OPEN_EVENT));
  } catch {
    /* хуучин хөтөч — товчоор нээнэ */
  }
};

/** Орон нутгийн өдөр (YYYY-MM-DD) — UTC биш, Улаанбаатарын цагаар */
const dayKey = () => new Date().toLocaleDateString("sv");

/* Өдөрт НЭГ удаа автоматаар нээх тэмдэглэл — ажилтан тус бүрд (нэг компьютер
   дээр олон хүн нэвтэрч болно). Хувийн цонхонд localStorage алга бол зүгээр
   л нээгдэнэ. */
const seenKey = (name) => `assistant_day_${name || "_"}`;
const readSeen = (name) => {
  try {
    return window.localStorage.getItem(seenKey(name));
  } catch {
    return null;
  }
};
const writeSeen = (name, day) => {
  try {
    window.localStorage.setItem(seenKey(name), day);
  } catch {
    /* хадгалах боломжгүй */
  }
};

const isMobileApp = () => {
  try {
    return window.localStorage.getItem("mobile_app") === "1";
  } catch {
    return false;
  }
};

/* ── Мессежийн бөмбөлөг ─────────────────────────────────────────────── */
function Bubble({ who, children }) {
  const bot = who === "bot";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: bot ? "flex-start" : "flex-end",
        gap: 6,
      }}
    >
      {bot ? (
        <div
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #CFFAFE",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            marginTop: 2,
          }}
        >
          <HseAiIcon size={30} />
        </div>
      ) : null}
      <div
        style={{
          maxWidth: "84%",
          background: bot ? "#fff" : GRAD,
          color: bot ? "#1E293B" : "#fff",
          border: bot ? "1px solid #CFFAFE" : "none",
          boxShadow: bot
            ? "0 1px 2px rgba(8,80,110,.06)"
            : "0 2px 8px rgba(8,145,178,.25)",
          padding: "8px 11px",
          borderRadius: bot ? "14px 14px 14px 4px" : "14px 14px 4px 14px",
          fontSize: 13.5,
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Системд ОРУУЛСАН заавар (хуудас бүрийн «?» тусламж — xab.help_topic).
 * Хэрэглэгч: «аюул бүртгэх заавар өгөөч гэх үед өөрийн системийн оруулсан
 * зааварыг харуулах».
 */
function GuideCard({ g, onGo }) {
  return (
    <div
      style={{
        marginTop: 8,
        background: "#fff",
        border: "1px solid #A5F3FC",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: "#ECFEFF",
          padding: "6px 10px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "#0E7490",
          fontSize: 11.5,
          fontWeight: 600,
        }}
      >
        <BookOutlined /> Системийн заавар
      </div>
      <div style={{ padding: "8px 10px" }}>
        <div style={{ fontWeight: 700, fontSize: 13.5, color: "#1E293B" }}>
          {g.title}
        </div>
        {g.intro ? (
          <div style={{ fontSize: 12.5, color: "#475569", marginTop: 2 }}>
            {g.intro}
          </div>
        ) : null}
        {g.steps?.length ? (
          <ol style={{ margin: "8px 0 0", paddingLeft: 0, listStyle: "none" }}>
            {g.steps.map((st, i) => (
              <li key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <span
                  style={{
                    flexShrink: 0,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: GRAD,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontWeight: 600,
                      fontSize: 12.5,
                    }}
                  >
                    {st.title}
                  </span>
                  {st.text ? (
                    <span
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#475569",
                      }}
                    >
                      {st.text}
                    </span>
                  ) : null}
                  {st.image_url ? (
                    <a href={st.image_url} target="_blank" rel="noreferrer">
                      <img
                        src={st.image_url}
                        alt={st.title}
                        style={{
                          marginTop: 4,
                          maxWidth: "100%",
                          maxHeight: 140,
                          borderRadius: 6,
                          border: "1px solid #E2E8F0",
                        }}
                      />
                    </a>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        ) : null}
        {g.notes?.length ? (
          <div
            style={{
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderRadius: 6,
              padding: "5px 8px",
              fontSize: 11.5,
              color: "#92400E",
            }}
          >
            {g.notes.slice(0, 3).map((n, i) => (
              <div key={i}>⚠ {n}</div>
            ))}
          </div>
        ) : null}
        <div
          style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}
        >
          <button type="button" onClick={() => onGo(g.link)} style={goBtn}>
            Хуудас руу очих →
          </button>
          {g.video_url ? (
            <a
              href={g.video_url}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 12.5, fontWeight: 600 }}
            >
              ▶ Видео заавар
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Зөрчил арилгах даалгавар — АЛХАМ БҮРИЙН заавар (+ AI зөвлөгөө).
 * Хэрэглэгч: «зөрчлийг арилгах даалгавар ирсэн үед тухайн хүнд хийх алхам
 * бүрийг зааж өгөх». Туслах өөрөө биелэлт оруулахгүй — хуудас руу хүргэнэ.
 */
function TaskGuideCard({ g, onGo }) {
  const t = g.task || {};
  const adv = g.advice;
  const tag = (bg, fg, txt) => (
    <span
      style={{
        background: bg,
        color: fg,
        borderRadius: 999,
        padding: "1px 8px",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {txt}
    </span>
  );
  return (
    <div
      style={{
        marginTop: 8,
        background: "#fff",
        border: "1px solid #A5F3FC",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div style={{ background: "#ECFEFF", padding: "7px 10px" }}>
        <div style={{ fontSize: 11, color: "#0E7490", fontWeight: 700 }}>
          {t.kind === "hazard"
            ? "Аюул арилгах даалгавар"
            : "Зөрчил арилгах даалгавар"}
          {t.no ? ` · ${t.no}` : ""}
          {t.inspection_code ? ` · ${t.inspection_code}` : ""}
        </div>
        <div
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: "#1E293B",
            marginTop: 2,
          }}
        >
          {t.text}
        </div>
        <div
          style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}
        >
          {t.is_overdue
            ? tag(
                "#FEE2E2",
                "#B91C1C",
                `Хугацаа хэтэрсэн · ${t.due_date || ""}`,
              )
            : t.due_date
              ? tag("#FEF3C7", "#92400E", `Хугацаа ${t.due_date}`)
              : tag("#F1F5F9", "#475569", "Хугацаа заагаагүй")}
          {t.reject_note ? tag("#FEE2E2", "#B91C1C", "Буцаагдсан") : null}
        </div>
      </div>
      <ol style={{ margin: 0, padding: "8px 10px 2px", listStyle: "none" }}>
        {(g.steps || []).map((st, i) => (
          <li key={i} style={{ display: "flex", gap: 8, marginBottom: 9 }}>
            <span
              style={{
                flexShrink: 0,
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: GRAD,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {i + 1}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{ display: "block", fontWeight: 600, fontSize: 12.5 }}
              >
                {st.title}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: 12,
                  color: "#475569",
                  whiteSpace: "pre-wrap",
                }}
              >
                {st.text}
              </span>
              {st.action ? (
                <button
                  type="button"
                  onClick={() => onGo(st.action.link)}
                  style={{
                    ...goBtn,
                    marginTop: 4,
                    padding: "2px 10px",
                    fontSize: 11.5,
                  }}
                >
                  {st.action.label} →
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
      {adv && adv.advice ? (
        <div
          style={{
            margin: "0 10px 8px",
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 8,
            padding: "7px 9px",
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: "#15803D",
              marginBottom: 3,
            }}
          >
            <HseAiIcon size={20} /> AI зөвлөгөө — юуг яаж засах вэ
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 16,
              fontSize: 12.5,
              color: "#1E293B",
            }}
          >
            {adv.advice
              .split(/\n+/)
              .filter(Boolean)
              .map((x, i) => (
                <li key={i}>{x}</li>
              ))}
          </ul>
          {adv.precautions?.length ? (
            <div style={{ marginTop: 5, fontSize: 12, color: "#92400E" }}>
              {adv.precautions.map((x, i) => (
                <div key={i}>⚠ {x}</div>
              ))}
            </div>
          ) : null}
          {adv.evidence ? (
            <div style={{ marginTop: 5, fontSize: 12, color: "#334155" }}>
              📷 Нотолгоо: {adv.evidence}
            </div>
          ) : null}
          {g.sources?.length ? (
            <div style={{ marginTop: 5, fontSize: 11, color: "#64748B" }}>
              Эх сурвалж:{" "}
              {g.sources
                .map((x) => x.code || x.title)
                .filter(Boolean)
                .join(" · ")}
            </div>
          ) : null}
        </div>
      ) : null}
      <div style={{ padding: "0 10px 8px", fontSize: 10.5, color: "#94A3B8" }}>
        {g.disclaimer}
      </div>
    </div>
  );
}

/**
 * 👍/👎 — туслахын СУРАЛЦАХ ДАВТАЛТ. 👎 нь ХАБЭА хэлтсийн «хариултгүй
 * асуулт»-д орж, тэдний бичсэн хариу дараагийн удаа туслахын мэдлэг болно.
 */
function Feedback({ logId }) {
  const [state, setState] = useState(null); // null · "good" · "bad-note" · "bad" · "sent"
  const [note, setNote] = useState("");
  const send = (rating, n) =>
    xabAskApi
      .askFeedback({ log_id: logId, rating, note: n || undefined })
      .then(() => setState(rating === 1 ? "good" : "bad"))
      .catch(() => setState("sent"));
  if (!logId) return null;
  if (state === "good") return <div style={fbText}>Баярлалаа! 👍</div>;
  if (state === "bad" || state === "sent")
    return (
      <div style={fbText}>
        Баярлалаа. ХАБЭА хэлтэст дамжууллаа — хариу бичвэл танд мэдэгдэл ирнэ.
      </div>
    );
  if (state === "bad-note")
    return (
      <div style={{ marginTop: 6, display: "flex", gap: 4 }}>
        <Input
          size="small"
          value={note}
          autoFocus
          onChange={(e) => setNote(e.target.value)}
          onPressEnter={() => send(-1, note)}
          placeholder="Юу дутуу байсан бэ? (заавал биш)"
          maxLength={300}
        />
        <button
          type="button"
          onClick={() => send(-1, note)}
          style={{ ...chip, flexShrink: 0 }}
        >
          Илгээх
        </button>
      </div>
    );
  return (
    <div
      style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}
    >
      <span style={{ fontSize: 11, color: "#64748B" }}>Тус болсон уу?</span>
      <button
        type="button"
        aria-label="Тустай"
        onClick={() => send(1)}
        style={fbBtn}
      >
        👍
      </button>
      <button
        type="button"
        aria-label="Тус болоогүй"
        onClick={() => setState("bad-note")}
        style={fbBtn}
      >
        👎
      </button>
    </div>
  );
}

/** ХАБЭА хэлтсийн өмнө бичсэн хариулт — хамгийн найдвартай эх сурвалж */
function QaCard({ items }) {
  return (
    <div
      style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}
    >
      {items.map((x) => (
        <div
          key={x.id}
          style={{
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 8,
            padding: "6px 9px",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: "#15803D" }}>
            ХАБЭА хэлтсийн хариулт{x.by ? ` · ${x.by}` : ""}
          </div>
          <div style={{ fontSize: 11.5, color: "#64748B" }}>«{x.q}»</div>
          <div
            style={{ fontSize: 12.5, color: "#1E293B", whiteSpace: "pre-wrap" }}
          >
            {x.a}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionRows({ sections, onOpen }) {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}
    >
      {sections.map((s) => {
        const c = LEVEL[s.level] || LEVEL.info;
        const first = s.lines && s.lines[0];
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onOpen(s)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              textAlign: "left",
              width: "100%",
              background: c.bg,
              border: `1px solid ${c.bd}`,
              borderRadius: 8,
              padding: "6px 8px",
              cursor: s.link ? "pointer" : "default",
            }}
          >
            <span style={{ color: c.fg, fontSize: 16 }}>
              {ICON[s.key] || <BellOutlined />}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: "#1E293B",
                }}
              >
                {s.title}
              </span>
              {first ? (
                <span
                  style={{
                    display: "block",
                    fontSize: 11.5,
                    color: "#64748B",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {first.text}
                  {first.tag ? ` · ${first.tag}` : ""}
                </span>
              ) : null}
            </span>
            <span style={{ fontWeight: 700, fontSize: 17, color: c.fg }}>
              {s.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function AssistantChat() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);
  const [today, setToday] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  // Аль ӨДРИЙН мэндчилгээ хийгдсэн — өдөр солигдвол дахин «Өнөөдрийн ажил»-аар эхэлнэ
  const greetedRef = useRef(null);

  const push = (m) =>
    setMsgs((xs) => [...xs, { id: Date.now() + Math.random(), ...m }]);

  const loadToday = useCallback(async () => {
    const d = await assistantApi.today();
    setToday(d);
    return d;
  }, []);

  // ── АЖЛЫН ӨДӨР БҮР «ӨНӨӨДРИЙН АЖИЛ»-ААР ЭХЭЛНЭ (2026-11-05) ─────────
  // Хэрэглэгч: «ажлын шинэ өдөр эхлэх бүрд тухайн хүний өнөөдрийн ажлаар
  // эхэлж байх хэрэгтэй». Өдрийн АНХНЫ нээлтэд чат өөрөө нээгдэж, өнөөдрийн
  // ажлыг харуулна. Хаавал тэр өдөртөө дахин өөрөө нээгдэхгүй. Таб шөнөжин
  // нээлттэй байсан ч өглөө буцаж ирэхэд (focus) шинэ өдрийг танина.
  const checkDay = useCallback(() => {
    loadToday()
      .then((d) => {
        if (isMobileApp()) return;
        const day = dayKey();
        if (readSeen(d?.name) !== day) {
          writeSeen(d?.name, day);
          setTimeout(() => setOpen(true), 1200);
        }
      })
      .catch(() => {});
  }, [loadToday]);

  useEffect(() => {
    checkDay();
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        greetedRef.current !== dayKey()
      ) {
        checkDay();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [checkDay]);

  const showToday = useCallback(
    async (fresh) => {
      setBusy(true);
      try {
        const d = fresh || !today ? await loadToday() : today;
        const active = (d.sections || []).filter((s) => s.count > 0);
        push({
          who: "bot",
          kind: "today",
          text: active.length
            ? `${d.greeting} Өнөөдөр танд анхаарах ${active.length} зүйл байна:`
            : `${d.greeting} Өнөөдөр танд хүлээгдэж буй ажил алга. Аюулгүй ажиллаарай.`,
          sections: active,
        });
      } catch {
        push({
          who: "bot",
          text: "Өнөөдрийн ажлыг татаж чадсангүй. Дахин оролдоно уу.",
        });
      } finally {
        setBusy(false);
      }
    },
    [today, loadToday],
  );

  useEffect(() => {
    const day = dayKey();
    if (open && greetedRef.current !== day) {
      // Өчигдрийн яриа үлдсэн бол шинэ өдрийг цэвэрээр эхлүүлнэ
      if (greetedRef.current) setMsgs([]);
      greetedRef.current = day;
      showToday(true);
    }
  }, [open, showToday]);

  useEffect(() => {
    if (bodyRef.current)
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, busy]);

  /** «Зөрчлөө яаж арилгах вэ» гэж асуусан үед л — даалгаврын жагсаалт */
  const showTasks = async () => {
    setBusy(true);
    try {
      const d = await loadToday();
      const tasks =
        (d.sections || []).find((s) => s.key === "tasks")?.tasks || [];
      push(
        tasks.length
          ? {
              who: "bot",
              kind: "tasks",
              text: "Зөрчил арилгах даалгавраа хэрхэн хийхийг алхам алхмаар зааж өгье. Алийг нь сонгох вэ?",
              tasks,
            }
          : {
              who: "bot",
              text: "Танд одоогоор хийх ёстой зөрчил арилгах даалгавар алга.",
            },
      );
    } catch {
      push({
        who: "bot",
        text: "Даалгавраа татаж чадсангүй. Дахин оролдоно уу.",
      });
    } finally {
      setBusy(false);
    }
  };

  const openTask = async (t) => {
    push({
      who: "me",
      text: `Яаж арилгах вэ: ${t.no || ""} ${t.text || ""}`.trim(),
    });
    setBusy(true);
    try {
      const g = await assistantApi.taskGuide(t.id);
      push({
        who: "bot",
        kind: "taskguide",
        text: g.advice?.advice
          ? "Энэ даалгаврыг дараах алхмаар хийнэ. Доор AI-ийн зөвлөгөө бий:"
          : "Энэ даалгаврыг дараах алхмаар хийнэ:",
        taskGuide: g,
      });
    } catch (e) {
      push({
        who: "bot",
        text:
          e?.response?.data?.message ||
          "Даалгаврын зааврыг татаж чадсангүй. Дахин оролдоно уу.",
      });
    } finally {
      setBusy(false);
    }
  };

  const openSection = (s) => {
    if (!s.link) return;
    navigate(s.link);
  };

  const ask = async (q) => {
    push({ who: "me", text: q });
    if (TASK_RE.test(q) && q.length < 80) return showTasks();
    if (TODAY_RE.test(q) && q.length < 40) return showToday(true);
    setBusy(true);
    try {
      // Сүүлийн хэдэн мессеж — «тэр заалт юу гэж байна» гэх мэт залгамж асуултад
      const history = msgs.slice(-6).map((m) => ({ who: m.who, text: m.text }));
      const r = await assistantApi.chat(q, history);
      const items = (r.sources || []).map((it) => ({
        ...it,
        source: it.group_key,
      }));
      if (r.answer) {
        push({
          who: "bot",
          kind: "ask",
          ai: true,
          text: r.answer,
          items: items.slice(0, 3),
          cited: r.cited || [],
          suggest: r.suggest || [],
          guide: r.guide || null,
          qa: r.qa || [],
          logId: r.log_id,
        });
      } else if (r.qa?.length) {
        push({
          who: "bot",
          kind: "ask",
          text: "ХАБЭА хэлтэс энэ асуултад өмнө нь хариулсан байна:",
          items: items.slice(0, 3),
          qa: r.qa,
          guide: r.guide || null,
          logId: r.log_id,
        });
      } else if (r.guide) {
        push({
          who: "bot",
          kind: "ask",
          text: "Системд оруулсан заавар:",
          items: [],
          guide: r.guide,
          logId: r.log_id,
        });
      } else if (!items.length) {
        push({
          who: "bot",
          logId: r.log_id,
          text: r.off_topic
            ? "Энэ асуулт ХАБЭА-гийн журам, зааварт хамааралгүй бололтой. Өөрөөр асууж үзнэ үү."
            : "Тохирох заалт олдсонгүй. Өөр үгээр асууж үзнэ үү, эсвэл БН-ийн ХАБ ажилтнаас лавлана уу.",
        });
      } else {
        push({
          who: "bot",
          kind: "ask",
          text: "Журам, зааварт ингэж байна:",
          items: items.slice(0, 3),
          logId: r.log_id,
        });
      }
    } catch {
      push({ who: "bot", text: "Хайлт амжилтгүй боллоо. Дахин оролдоно уу." });
    } finally {
      setBusy(false);
    }
  };

  const onChip = (k) => {
    if (k === "today") {
      push({ who: "me", text: "Өнөөдрийн ажил" });
      showToday(true);
    } else if (k === "hazard") {
      navigate("/hazard/register");
    }
  };

  const send = () => {
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    ask(q);
  };

  // Мобайл апп-ын WebView дотор — native апп өөрийн туслахтай тул давхардуулахгүй
  if (isMobileApp()) return null;

  const urgent = (today?.sections || []).some(
    (s) => s.level === "danger" && s.count > 0,
  );
  const total = (today?.sections || []).filter((s) => s.count > 0).length;

  return (
    <>
      <style>{PULSE_CSS}</style>
      {open ? (
        <div
          role="dialog"
          aria-label="ХАБЭА туслах"
          style={{
            position: "fixed",
            right: 16,
            bottom: 96,
            width: "min(380px, calc(100vw - 32px))",
            height: "min(560px, calc(100vh - 120px))",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 16px 48px rgba(8,80,110,.30)",
            zIndex: 1001,
            animation: "hseAsstIn .18s ease-out",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: GRAD,
              color: "#fff",
              padding: "12px 12px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HseAiIcon size={38} />
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14.5 }}>
                ХАБЭА туслах
              </div>
              <div style={{ fontSize: 11.5, opacity: 0.85 }}>
                Өнөөдрийн ажил · журмаас асуух
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Хаах"
              style={hdrBtn}
            >
              <CloseOutlined />
            </button>
          </div>

          <div
            ref={bodyRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background: "linear-gradient(180deg, #ECFEFF 0%, #F0F9FF 100%)",
            }}
          >
            {msgs.map((m) => (
              <Bubble key={m.id} who={m.who}>
                {m.text}
                {m.qa?.length ? <QaCard items={m.qa} /> : null}
                {m.kind === "tasks" && m.tasks?.length ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      marginTop: 6,
                    }}
                  >
                    {m.tasks.map((t) => (
                      <div
                        key={t.id}
                        style={{
                          background: t.is_overdue ? "#FEF2F2" : "#fff",
                          border: `1px solid ${t.is_overdue ? "#FECACA" : "#A5F3FC"}`,
                          borderRadius: 8,
                          padding: "6px 8px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: t.is_overdue ? "#B91C1C" : "#64748B",
                          }}
                        >
                          {t.no ? `${t.no} · ` : ""}
                          {t.is_overdue
                            ? "хугацаа хэтэрсэн"
                            : t.due_date
                              ? `${t.due_date} хүртэл`
                              : "хугацаагүй"}
                          {t.returned ? " · буцаагдсан" : ""}
                        </div>
                        <div style={{ fontSize: 12.5, color: "#1E293B" }}>
                          {t.text}
                        </div>
                        <button
                          type="button"
                          onClick={() => !busy && openTask(t)}
                          style={{
                            ...goBtn,
                            marginTop: 4,
                            padding: "2px 10px",
                            fontSize: 11.5,
                          }}
                        >
                          Алхмаар харуулах
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                {m.kind === "taskguide" && m.taskGuide ? (
                  <TaskGuideCard
                    g={m.taskGuide}
                    onGo={(link) => {
                      setOpen(false);
                      navigate(link);
                    }}
                  />
                ) : null}
                {m.guide ? (
                  <GuideCard
                    g={m.guide}
                    onGo={(link) => {
                      setOpen(false);
                      navigate(link);
                    }}
                  />
                ) : null}
                {m.kind === "today" && m.sections?.length ? (
                  <SectionRows sections={m.sections} onOpen={openSection} />
                ) : null}
                {m.kind === "ask" ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      marginTop: 6,
                    }}
                  >
                    {m.ai ? (
                      <div style={{ fontSize: 11, color: "#64748B" }}>
                        AI хариулт · доорх эх сурвалжаас шалгана уу
                      </div>
                    ) : null}
                    {m.items.map((it) =>
                      m.ai ? (
                        <div
                          key={`${it.source}-${it.id}`}
                          style={{
                            fontSize: 11.5,
                            color: "#334155",
                            background: "#fff",
                            border: "1px solid #E2E8F0",
                            borderRadius: 6,
                            padding: "3px 7px",
                          }}
                        >
                          <BookOutlined
                            style={{ marginRight: 4, color: BRAND }}
                          />
                          {it.code || it.group}
                          {it.title ? ` — ${it.title}` : ""}
                        </div>
                      ) : (
                        <div
                          key={`${it.source}-${it.id}`}
                          style={{
                            background: "#fff",
                            border: "1px solid #E2E8F0",
                            borderRadius: 8,
                            padding: "6px 8px",
                          }}
                        >
                          <div style={{ fontSize: 11, color: "#64748B" }}>
                            {it.group}
                            {it.code ? ` · ${it.code}` : ""}
                          </div>
                          {it.title ? (
                            <div style={{ fontWeight: 600, fontSize: 12.5 }}>
                              {it.title}
                            </div>
                          ) : null}
                          {it.text ? (
                            <div style={{ fontSize: 12.5, color: "#334155" }}>
                              {String(it.text).slice(0, 260)}
                              {String(it.text).length > 260 ? "…" : ""}
                            </div>
                          ) : null}
                        </div>
                      ),
                    )}
                    {m.suggest?.length ? (
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                      >
                        {m.suggest.map((sg) => (
                          <button
                            key={sg}
                            type="button"
                            onClick={() => !busy && ask(sg)}
                            style={{
                              ...chip,
                              fontSize: 11.5,
                              textAlign: "left",
                            }}
                          >
                            {sg}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {m.items?.length ? (
                      <button
                        type="button"
                        onClick={() => navigate("/knowledge/ask")}
                        style={{ ...linkBtn }}
                      >
                        «Журмаас асуух» хуудсанд дэлгэрэнгүй →
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {m.who === "bot" && m.logId ? (
                  <Feedback logId={m.logId} />
                ) : null}
              </Bubble>
            ))}
            {busy ? (
              <Bubble who="bot">
                <Spin size="small" />{" "}
                <span style={{ color: "#64748B" }}>Бодож байна…</span>
              </Bubble>
            ) : null}
          </div>

          <div
            style={{
              padding: "6px 10px 0",
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            {CHIPS.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onChip(c.key)}
                style={chip}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div style={{ padding: 10, display: "flex", gap: 6 }}>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPressEnter={send}
              placeholder="Асуух зүйлээ бичнэ үү… (жш: өндөрт ажиллах)"
              maxLength={300}
              aria-label="Туслахаас асуух"
            />
            <button
              type="button"
              onClick={send}
              disabled={!text.trim() || busy}
              aria-label="Илгээх"
              style={{ ...sendBtn, opacity: !text.trim() || busy ? 0.5 : 1 }}
            >
              <SendOutlined />
            </button>
          </div>
        </div>
      ) : null}

      <Tooltip title={open ? "" : "ХАБЭА туслах"} placement="left">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Туслахыг хаах" : "ХАБЭА туслахыг нээх"}
          style={{
            position: "fixed",
            // ⚠ Дэлгэцийн ДОТОГШ (2026-11-05): баруун захад байхад хүснэгтийн
            //   наалттай «Үйлдэл» баганын (stickyActions.css) товчийг дарж
            //   дарагдахгүй болгож байв. Үйлдлийн багана ихэвчлэн 90–220px тул
            //   түүнээс зүүн тийш; нарийн дэлгэцэд 18vw хүртэл ойртоно.
            right: "clamp(64px, 18vw, 260px)",
            bottom: 20,
            width: 68,
            height: 68,
            borderRadius: "50%",
            border: "3px solid transparent",
            backgroundImage: `linear-gradient(#fff, #fff), ${GRAD}`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
            color: BRAND,
            fontSize: 24,
            boxShadow: "0 8px 24px rgba(8,145,178,.45)",
            // Анхаарах ажил байхад зөөлөн «цохилт» — хаалттай үед л
            animation: !open && total ? "hseAsstPulse 2s infinite" : "none",
            cursor: "pointer",
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Badge
            count={open ? 0 : total}
            size="small"
            offset={[6, -6]}
            color={urgent ? "#DC2626" : "#F59E0B"}
          >
            {open ? (
              <CloseOutlined style={{ color: BRAND, fontSize: 22 }} />
            ) : (
              <HseAiIcon size={56} title="ХАБЭА туслах" />
            )}
          </Badge>
        </button>
      </Tooltip>
    </>
  );
}

const hdrBtn = {
  background: "rgba(255,255,255,.16)",
  border: "none",
  color: "#fff",
  width: 32,
  height: 32,
  borderRadius: "50%",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
};
const chip = {
  border: `1px solid ${BRAND}55`,
  background: "#fff",
  color: BRAND,
  borderRadius: 999,
  padding: "3px 10px",
  fontSize: 12,
  cursor: "pointer",
};
const sendBtn = {
  border: "none",
  background: GRAD,
  color: "#fff",
  width: 38,
  borderRadius: 8,
  cursor: "pointer",
  flexShrink: 0,
};
const fbBtn = {
  border: "1px solid #CFFAFE",
  background: "#fff",
  borderRadius: 999,
  padding: "1px 9px",
  cursor: "pointer",
  fontSize: 13,
};
const fbText = { marginTop: 6, fontSize: 11.5, color: "#0E7490" };
const goBtn = {
  border: "none",
  background: GRAD,
  color: "#fff",
  borderRadius: 999,
  padding: "4px 12px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
const linkBtn = {
  border: "none",
  background: "transparent",
  color: BRAND,
  padding: 0,
  textAlign: "left",
  cursor: "pointer",
  fontSize: 12.5,
  fontWeight: 600,
};
